import type { Release } from '@/lib/api/schema';
import { parsePage } from '@/lib/api/schema';
import { SAMPLE_RELEASES } from '@/lib/sampleData';

export type DataSource = 'live' | 'sample';

export interface ReleasesResult {
  releases: Release[];
  source: DataSource;
  /** Resolved base URL the data came from, or a label for the sample set. */
  origin: string;
  fetchedAt: number;
}

/** Trim trailing slashes so we can safely append the export path. */
export function normalizeBase(base: string): string {
  return base.trim().replace(/\/+$/, '');
}

/**
 * Walk the cursor-paginated export endpoint until exhausted. A guard caps the
 * walk at 40 pages (40k records at limit=1000) to avoid a runaway loop on a
 * misbehaving cursor.
 */
async function fetchAllReleases(base: string, apiKey?: string, signal?: AbortSignal): Promise<Release[]> {
  const all: Release[] = [];
  let cursor: string | null = null;
  let guard = 0;

  do {
    const url =
      `${base}/api/v1/export/releases?limit=1000` +
      (cursor ? `&cursor=${encodeURIComponent(cursor)}` : '');

    const headers: Record<string, string> = { Accept: 'application/json' };
    if (apiKey) headers['X-API-Key'] = apiKey;

    const response = await fetch(url, {
      headers,
      signal: signal ?? null,
    });
    if (!response.ok) throw new Error(`Export feed returned HTTP ${response.status}`);

    const { items, nextCursor } = parsePage(await response.json());
    all.push(...items);
    cursor = nextCursor;
    guard += 1;
  } while (cursor && guard < 40);

  return all;
}

/**
 * Load all releases from the live feed, transparently falling back to the
 * embedded sample set when the network/CORS/parse path fails. The returned
 * `source` tells the UI which path was taken so it can label the connection.
 */
export async function loadReleases(
  rawBase: string,
  apiKey?: string,
  signal?: AbortSignal,
): Promise<ReleasesResult> {
  const base = normalizeBase(rawBase);
  try {
    const releases = await fetchAllReleases(base, apiKey, signal);
    // An empty 200 is still "live" but unhelpful; surface the sample instead.
    if (releases.length === 0) {
      return {
        releases: SAMPLE_RELEASES,
        source: 'sample',
        origin: 'embedded sample data',
        fetchedAt: Date.now(),
      };
    }
    return {
      releases,
      source: 'live',
      origin: base,
      fetchedAt: Date.now(),
    };
  } catch (error) {
    // Re-throw genuine cancellations so React Query treats them correctly.
    if (error instanceof DOMException && error.name === 'AbortError') throw error;
    return {
      releases: SAMPLE_RELEASES,
      source: 'sample',
      origin: 'embedded sample data',
      fetchedAt: Date.now(),
    };
  }
}
